import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, UserSettings } from '@core/types';
import {
  User,
  UserCreate,
  UserUpdate,
  UserGridConfig,
  UserPaginationResult,
} from '@core/types/user.type';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.api}/user`;

  getGrid(config: UserGridConfig): Observable<ApiResponse<UserPaginationResult>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());
    if (config.searchTerm != null && config.searchTerm !== '') {
      params = params.set('searchTerm', config.searchTerm);
    }
    return this.http.get<ApiResponse<UserPaginationResult>>(`${this.apiUrl}/grid`, {
      params,
    });
  }

  get(id: string): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/${id}`);
  }

  create(data: UserCreate): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(this.apiUrl, data);
  }

  update(data: UserUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.apiUrl, data);
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  getSettings(): Observable<ApiResponse<UserSettings>> {
    return this.http.get<ApiResponse<UserSettings>>(`${this.apiUrl}/settings`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading user settings')
      )
    );
  }

  updateSettings(data: UserSettings): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/settings`, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error updating user settings')
      )
    );
  }
}
